import { useState, useEffect } from "react";

import {
  createEventSubscriptionObservableList,
  createEventSubscriptionConstants,
} from "models/event";

import { serviceGraphCall, serviceLogError } from "services/graphql/call";

export const useEventSubscriptions = (options = {}) => {
  const { user, state, dispatch, actions, callbacks, pause } = options;

  const subscriptionConstants = createEventSubscriptionConstants();
  const subscriptionObservableList = createEventSubscriptionObservableList();

  // overall parent state for subscriptions activation for a given eventuator
  const [subsActiveEventId, setSubsActiveEventId] = useState(null);

  // track individual subscription states based on keys from observable list
  const [subsMap, setSubsMap] = useState(
    subscriptionConstants.observableList.reduce((acc, type) => {
      acc[type] = {
        active: false,
        params: {},
        message: null,
        subscription: null,
      };
      return acc;
    }, {}),
  );

  // initiate effect to activate/deactivate subscriptions based on event and user state
  useEffect(() => {
    // handler to execute actions on observable callback
    const callbackAction = async (error, observable, result) => {
      dispatch({
        type: actions.SET_CALLBACK_LOADER,
        callbackLoader: false,
      });
      if (error) {
        serviceLogError("observableCallback", { error, observable, result });
        return;
      }

      // default dispatch based on action definition
      const payload = {
        type: observable.action.type,
      };
      payload[observable.action.property] = result;
      dispatch(payload);

      // custom dispatches based on action flags
      // - recalculate module headers and counters
      // - recalendar event calendar
      // - reticket viewer tickets
      if (observable.action.recalculate) {
        dispatch({ type: actions.GENERATE_MODULES });
      }
      if (observable.action.recalendar) {
        const eventData = await callbacks.cbPreparePullEventData({
          EventId: state.event.data.EventId,
          Url: state.event.data.Url,
          user: user,
        });
        if (eventData.alert) {
          dispatch({
            type: actions.UPDATE_ALERT,
            alert: { show: true, message: eventData.message },
          });
        } else {
          dispatch({
            type: actions.RELOAD_EVENT_DATA_TICKING,
            data: eventData.data,
          });
          dispatch({
            type: actions.GENERATE_CALENDAR,
          });
        }
      }
      if (observable.action.reticket) {
        const viewerTickets = await callbacks.cbPreparePullViewerTickets({
          EventId: state.event.data.EventId,
          user: user,
        });
        if (viewerTickets.alert) {
          dispatch({
            type: actions.UPDATE_ALERT,
            alert: { show: true, message: viewerTickets.message },
          });
        } else {
          dispatch({
            type: actions.PULL_VIEWER_TICKETS,
            tickets: viewerTickets,
          });
        }
      }
    };

    // handler to open a target subscription
    const openSubscription = async (type, params) => {
      // validate subscription definition and state before opening
      if (
        !subscriptionObservableList[type].field ||
        !subscriptionObservableList[type].action
      ) {
        console.error(`Unable to start sub ${type}: Invalid definition.`);
        return;
      }
      if (
        subscriptionObservableList[type].modules &&
        subscriptionObservableList[type].modules.length > 0 &&
        !subscriptionObservableList[type].modules.includes(
          state.event.moduleSelected,
        )
      ) {
        console.debug(
          `Skipping sub ${type}: Not relevant for module ${state.event.moduleSelected}.`,
        );
        return;
      }
      if (subsMap[type].active) {
        console.debug(`Skipping sub ${type}: Already active.`);
        return;
      }
      // set subscription payload
      // check for custom params or fallback to definition
      // handle special for desk messaging before and after call
      const requestParams =
        params || subscriptionObservableList[type].params || {};
      const customParams = { ...requestParams };
      if (customParams.RoomName === "desk" && !state.event.manager) {
        customParams.RoomName = "desk:" + user.identity;
      }

      // execute subscription call and set state based on response
      const callParams = {
        Url: state.event.data.Url,
        EventId: state.event.data.EventId,
        ...customParams,
      };
      const response = await serviceGraphCall(
        "subscription",
        subscriptionObservableList[type].field,
        callParams,
        {
          observable: subscriptionObservableList[type],
          call: callbackAction,
        },
      );
      setSubsMap((prev) => ({
        ...prev,
        [type]: {
          ...prev[type],
          active: response.success,
          params: requestParams,
          message: response.message,
          subscription: response.success ? response.data : null,
        },
      }));
    };

    // handler to close a target subscription
    const closeSubscription = async (type) => {
      if (subsMap[type] && subsMap[type].active && subsMap[type].subscription) {
        subsMap[type].subscription.unsubscribe();
        setSubsMap((prev) => ({
          ...prev,
          [type]: {
            ...prev[type],
            active: false,
            params: {},
            message: null,
            subscription: null,
          },
        }));
      }
    };

    // handler to open all subscriptions
    const openAllSubscriptions = async () => {
      setSubsActiveEventId(state.event.data.EventId);
      subscriptionConstants.observableList.forEach((type) => {
        openSubscription(type);
      });
    };

    // handler to close all subscriptions
    const closeAllSubscriptions = async () => {
      setSubsActiveEventId(null);
      subscriptionConstants.observableList.forEach((type) => {
        closeSubscription(type);
      });
    };

    // effect triggers
    if (user.connected && state.event.hydrated) {
      if (subsActiveEventId !== state.event.data.EventId && !pause) {
        // open all subs when connected
        openAllSubscriptions();
      } else if (pause && subsActiveEventId === state.event.data.EventId) {
        // pause all subs when idle
        closeAllSubscriptions();
      } else {
        // open/close relevant subs on module change
        subscriptionConstants.observableList.forEach((type) => {
          // any override conditions to restart subscription
          if (
            type === "messaging" &&
            state.event.moduleSelected === "messaging"
          ) {
            if (
              state.messaging.activeRoom !== subsMap.messaging.params.RoomName
            ) {
              closeSubscription(type);
              openSubscription(type, {
                RoomName: state.messaging.activeRoom,
              });
            }
          }

          if (
            subscriptionObservableList[type].modules &&
            subscriptionObservableList[type].modules.length > 0
          ) {
            if (
              subscriptionObservableList[type].modules.includes(
                state.event.moduleSelected,
              )
            ) {
              if (!subsMap[type].active) {
                openSubscription(type);
              }
            } else {
              if (subsMap[type].active) {
                closeSubscription(type);
              }
            }
          }
        });
      }
    }

    return () => {
      // effect unmount trigger: close all subs when connected and exiting eventuator
      if (
        user.connected &&
        state.event.data.EventId === null &&
        subsActiveEventId !== null
      ) {
        closeAllSubscriptions();
      }
    };
  }, [
    subscriptionConstants,
    subscriptionObservableList,
    user,
    state.event.hydrated,
    state.event.data.Url,
    state.event.data.EventId,
    state.event.moduleSelected,
    state.messaging.activeRoom,
    state.event.manager,
    dispatch,
    actions,
    callbacks,
    pause,
    subsActiveEventId,
    subsMap,
  ]);

  return [subsActiveEventId, subsMap];
};
